import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Shield, LogOut, User, ChevronDown, ArrowLeft, Building2, Menu, X, Settings, ChevronRight } from 'lucide-react';
import { NovaLogo } from './NovaLogo';
import { ThemeToggle } from './ThemeProvider';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

/**
 * Common Header Component
 * 
 * This is a shared header component used across ALL public-facing and authenticated pages.
 * 
 * PUBLIC PAGES (always add new public pages to isPublicPage array):
 * - Home (/)
 * - About Us (/about)
 * - Pricing (/pricing)
 * - Contact (/contact)
 * - Login/Signup (/login)
 * - DPA (/dpa)
 * - Privacy Policy (/privacy-policy)
 * - Terms of Service (/terms-of-service)
 * 
 * FEATURES:
 * - Public pages: Show Home/About Us/Pricing nav + Sign In/Get Started buttons
 * - Authenticated pages: Show role-based navigation + user menu
 * - Responsive design with mobile/desktop variations
 * 
 * IMPORTANT: When creating new public pages, always:
 * 1. Import this Header component
 * 2. Add the route to the isPublicPage array below
 * 3. Use <Header /> at the top of your page component
 */
const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, currentWorkspace, workspaces, switchWorkspace, logout, returnToAdmin } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path) => location.pathname === path;
  
  // Get user initials (first letter of first name + first letter of last name)
  const getUserInitials = () => {
    const name = user?.full_name || user?.email?.split('@')[0] || '';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase() || 'U';
  };
  
  // Check if we're on a public page
  // IMPORTANT: Add new public page routes here to maintain consistent header
  const isPublicPage = ['/', '/about', '/features', '/pricing', '/security', '/trust', '/contact', '/login', '/register', '/dpa', '/privacy-policy', '/terms-of-service'].includes(location.pathname);
  
  // Check if super admin is emulating (check for emulated_user flag)
  const isEmulating = localStorage.getItem('emulated_user') === 'true';

  // Get user's role in current workspace
  const currentRole = currentWorkspace?.role || 'user';

  return (
    <div className="bg-[#FAFAFA] dark:bg-[#050505] border-b border-black/10 dark:border-white/10 sticky top-0 z-50 relative px-4 sm:px-6">
      <div className="max-w-7xl mx-auto h-16 flex items-center">
        <div className="flex items-center w-full">
          {/* Logo — original brand mark */}
          <div
            className="flex items-center space-x-2 sm:space-x-3 cursor-pointer shrink-0"
            onClick={() => {
              if (!user) {
                navigate('/');
              } else if (currentRole === 'auditor') {
                navigate('/auditor/dashboard');
              } else if (currentRole === 'employee') {
                navigate('/employee/portal');
              } else {
                navigate('/dashboard');
              }
            }}
            data-testid="logo"
          >
            <NovaLogo markClassName="h-7 w-7 sm:h-8 sm:w-8" textClassName="text-xl sm:text-2xl" />
          </div>

          {/* Navigation - Centered */}
          <div className="flex-1 flex items-center justify-center space-x-4">
            {/* Public Navigation - Key items inline */}
            {isPublicPage && (
              <div className="hidden lg:flex items-center gap-1">
                {[
                  { path: '/', label: 'Home', testId: 'nav-home' },
                  { path: '/features', label: 'Features', testId: 'nav-features' },
                  { path: '/security', label: 'Security', testId: 'nav-security' },
                  { path: '/pricing', label: 'Pricing', testId: 'nav-pricing' },
                  { path: '/trust', label: 'Trust Center', testId: 'nav-trust' },
                  { path: '/contact', label: 'Contact', testId: 'nav-contact' },
                ].map((item) => (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    data-testid={item.testId}
                    className={`font-label uppercase tracking-[0.18em] text-[11px] px-3 py-2 rounded-full transition-colors ${
                      isActive(item.path)
                        ? 'text-[#0A0A0A] dark:text-[#F5F5F5] bg-[#D4E0DA]/55 dark:bg-[#101F18]/55'
                        : 'text-black/65 dark:text-white/65 hover:text-[#0A0A0A] dark:hover:text-[#F5F5F5] hover:bg-black/[0.04] dark:hover:bg-white/[0.06]'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}

            {/* Application Navigation - Only show inside application */}
            {!isPublicPage && user && (currentRole === 'owner' || currentRole === 'admin') && (
              <div className="hidden md:flex items-center gap-6">
                {[
                  { path: '/dashboard', label: 'Dashboard', match: (p) => p === '/dashboard' },
                  { path: '/audit-profile', label: 'Audit Profile', testId: 'nav-audit-profile', match: (p) => p.startsWith('/audit-profile') },
                  { path: '/employees', label: 'Employees', match: (p) => p.startsWith('/employees') },
                  { path: '/policies', label: 'Policies', match: (p) => p.startsWith('/policies') },
                  { path: '/trainings', label: 'Trainings', match: (p) => p.startsWith('/trainings') },
                  { path: '/technologies', label: 'Technology', match: (p) => p.startsWith('/technologies') },
                  { path: '/devices', label: 'Devices', testId: 'nav-devices', match: (p) => p.startsWith('/devices') },
                  { path: '/risks', label: 'Risks', testId: 'nav-risks', match: (p) => p.startsWith('/risks') },
                  { path: '/vapt', label: 'VAPT', testId: 'nav-vapt', match: (p) => p.startsWith('/vapt') },
                  { path: '/trust-center', label: 'Trust Center', testId: 'nav-trust-center', match: (p) => p.startsWith('/trust-center') },
                ].map((item) => (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    data-testid={item.testId}
                    className={`font-label uppercase tracking-wide text-[11px] pb-1 border-b-2 transition-colors ${
                      item.match(location.pathname) ? 'text-[#0F62FE] dark:text-[#5C97FF] border-[#0F62FE] dark:border-[#5C97FF] font-semibold' : 'text-[#64748B] dark:text-white/55 border-transparent hover:text-[#0A0A0A] dark:hover:text-[#F5F5F5]'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}

            {/* Auditor Navigation */}
            {!isPublicPage && user && currentRole === 'auditor' && (
              <div className="hidden md:flex items-center gap-5">
                <button
                  onClick={() => navigate('/auditor/dashboard')}
                  className={`font-label uppercase tracking-[0.16em] text-[11px] transition-colors ${
                    isActive('/auditor/dashboard') ? 'text-[#0A0A0A] dark:text-[#F5F5F5]' : 'text-black/55 dark:text-white/55 hover:text-[#0A0A0A] dark:hover:text-[#F5F5F5]'
                  }`}
                >
                  Dashboard
                </button>
              </div>
            )}

          </div>

          {/* User Avatar - Right aligned */}
          <div className="flex items-center gap-3 shrink-0">
            {user ? (
              // Logged in user
              !isPublicPage ? (
                // Inside application - consolidated user menu
                <>
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className="hidden md:flex items-center gap-2 hover:bg-black/[0.04] dark:hover:bg-white/[0.06] rounded-full pl-1.5 pr-3 py-1.5 h-auto"
                        data-testid="user-menu-trigger"
                      >
                        <div className="w-8 h-8 rounded-full bg-[#0F62FE] dark:bg-[#0F62FE] text-white flex items-center justify-center text-xs font-semibold tracking-tight font-label">
                          {getUserInitials()}
                        </div>
                        <ChevronDown className="w-3.5 h-3.5 text-black/40 dark:text-white/45" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-72 p-0 border-black/10 dark:border-white/10 shadow-[0_8px_30px_rgba(0,0,0,0.06)] bg-[#FAFAFA] dark:bg-[#050505]">
                      {/* User info header */}
                      <div className="px-4 py-3.5 bg-[#D4E0DA]/35 dark:bg-[#101F18]/35 border-b border-black/10 dark:border-white/10 rounded-t-md">
                        <div className="flex items-center gap-3">
                          <div className="w-11 h-11 rounded-full bg-[#0F62FE] dark:bg-[#0F62FE] text-white flex items-center justify-center text-sm font-semibold tracking-tight font-label">
                            {getUserInitials()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-display text-sm text-[#0A0A0A] dark:text-[#F5F5F5]">{user.full_name || user.email}</p>
                            <p className="text-xs text-black/55 dark:text-white/55">{user.email}</p>
                            <Badge className="capitalize text-[10px] px-2 py-0 mt-1 bg-[#0A0A0A] dark:bg-[#141414] text-[#FAFAFA] border-none hover:bg-[#0A0A0A] dark:hover:bg-[#141414] font-label tracking-wider">
                              {currentRole?.replace('_', ' ')}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      
                      {/* Current Workspace */}
                      {currentWorkspace && (
                        <div className="px-4 py-3 border-b border-black/10 dark:border-white/10">
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-md bg-[#D4E0DA]/60 dark:bg-[#101F18]/60 flex items-center justify-center">
                              <Building2 className="w-3.5 h-3.5 text-[#0A0A0A] dark:text-[#F5F5F5]" />
                            </div>
                            <div>
                              <p className="font-label text-[10px] uppercase tracking-[0.18em] text-black/50 dark:text-white/55 leading-none mb-1">Workspace</p>
                              <p className="text-sm font-medium text-[#0A0A0A] dark:text-[#F5F5F5]">{currentWorkspace.name}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Workspace Switcher */}
                      {workspaces && workspaces.length > 1 && currentWorkspace && (
                        <>
                          <div className="px-4 pt-2.5 pb-1">
                            <p className="font-label text-[10px] text-black/50 dark:text-white/55 uppercase tracking-[0.18em]">Switch Workspace</p>
                          </div>
                          {workspaces.filter(ws => ws.id !== currentWorkspace.id).map((workspace) => (
                            <DropdownMenuItem
                              key={workspace.id}
                              onClick={() => switchWorkspace(workspace.id)}
                              className="cursor-pointer mx-2 rounded-lg px-2.5 py-2 hover:bg-black/[0.04] dark:hover:bg-white/[0.06] transition-colors"
                            >
                              <div className="w-6 h-6 rounded-md bg-[#EBD8C8]/60 dark:bg-[#2E1B10]/60 flex items-center justify-center mr-2.5">
                                <Building2 className="w-3.5 h-3.5 text-[#0A0A0A] dark:text-[#F5F5F5]" />
                              </div>
                              <span className="text-sm text-black/75 dark:text-white/75 flex-1">{workspace.name}</span>
                              <ChevronRight className="w-3.5 h-3.5 text-black/30 dark:text-white/40" />
                            </DropdownMenuItem>
                          ))}
                          <DropdownMenuSeparator className="my-1.5 bg-black/10 dark:bg-white/10" />
                        </>
                      )}

                      {/* Navigation Items */}
                      <div className="py-1.5">
                        <DropdownMenuItem
                          onClick={() => navigate('/profile')}
                          className="cursor-pointer mx-2 rounded-lg px-2.5 py-2.5 hover:bg-black/[0.04] dark:hover:bg-white/[0.06] transition-colors"
                        >
                          <div className="w-6 h-6 rounded-md bg-[#D4E0DA]/60 dark:bg-[#101F18]/60 flex items-center justify-center mr-2.5">
                            <User className="w-3.5 h-3.5 text-[#0A0A0A] dark:text-[#F5F5F5]" />
                          </div>
                          <span className="text-sm text-black/75 dark:text-white/75">My Profile</span>
                        </DropdownMenuItem>

                        {currentWorkspace?.role !== 'super_admin' && (
                          <DropdownMenuItem
                            onClick={() => navigate('/workspace/settings')}
                            className="cursor-pointer mx-2 rounded-lg px-2.5 py-2.5 hover:bg-black/[0.04] dark:hover:bg-white/[0.06] transition-colors"
                          >
                            <div className="w-6 h-6 rounded-md bg-[#D9CFD9]/60 dark:bg-[#1E1724]/60 flex items-center justify-center mr-2.5">
                              <Settings className="w-3.5 h-3.5 text-[#0A0A0A] dark:text-[#F5F5F5]" />
                            </div>
                            <span className="text-sm text-black/75 dark:text-white/75">Workspace Settings</span>
                          </DropdownMenuItem>
                        )}
                      </div>

                      <DropdownMenuSeparator className="my-1 bg-black/10 dark:bg-white/10" />

                      {/* Return to Admin - only when emulating */}
                      {isEmulating && (
                        <>
                          <div className="mx-2 my-1.5 rounded-lg bg-[#D9CFD9]/40 dark:bg-[#1E1724]/40 border border-black/10 dark:border-white/10">
                            <DropdownMenuItem
                              onClick={returnToAdmin}
                              className="cursor-pointer rounded-lg px-2.5 py-2.5 hover:bg-[#D9CFD9]/70 dark:hover:bg-[#1E1724]/70 transition-colors"
                            >
                              <div className="w-6 h-6 rounded-md bg-[#0A0A0A] dark:bg-[#141414] flex items-center justify-center mr-2.5">
                                <ArrowLeft className="w-3.5 h-3.5 text-[#FAFAFA]" />
                              </div>
                              <span className="text-sm font-semibold text-[#0A0A0A] dark:text-[#F5F5F5]">Return to Admin</span>
                            </DropdownMenuItem>
                          </div>
                        </>
                      )}

                      {/* Logout */}
                      <div className="py-1.5 pb-2">
                        <DropdownMenuItem
                          onClick={() => {
                            logout();
                            navigate('/login');
                          }}
                          className="cursor-pointer mx-2 rounded-lg px-2.5 py-2.5 hover:bg-black/[0.04] dark:hover:bg-white/[0.06] transition-colors"
                        >
                          <div className="w-6 h-6 rounded-md border border-black/15 dark:border-white/15 flex items-center justify-center mr-2.5">
                            <LogOut className="w-3.5 h-3.5 text-[#0A0A0A] dark:text-[#F5F5F5]" />
                          </div>
                          <span className="text-sm font-medium text-[#0A0A0A] dark:text-[#F5F5F5]">Logout</span>
                        </DropdownMenuItem>
                      </div>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  
                  {/* Mobile Logout Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      logout();
                      navigate('/login');
                    }}
                    className="md:hidden border-black/15 dark:border-white/15 text-[#0A0A0A] dark:text-[#F5F5F5] bg-transparent hover:bg-black/[0.04] dark:hover:bg-white/[0.06]"
                  >
                    <LogOut className="w-4 h-4" />
                  </Button>
                </>
              ) : (
                // On public pages - show go to dashboard
                <Button
                  onClick={() => {
                    // Check workspace role for routing
                    if (currentWorkspace?.role === 'super_admin') {
                      navigate('/admin');
                    } else if (currentWorkspace?.role === 'employee') {
                      navigate('/employee/portal');
                    } else if (currentWorkspace?.role === 'auditor') {
                      navigate('/auditor/dashboard');
                    } else {
                      navigate('/dashboard');
                    }
                  }}
                  className="font-label uppercase tracking-[0.18em] text-[11px] bg-[#0A0A0A] dark:bg-[#141414] text-[#FAFAFA] hover:bg-[#EBD8C8] dark:hover:bg-[#2E1B10] hover:text-[#0A0A0A] dark:hover:text-[#F5F5F5] rounded-full px-5 py-2 h-auto transition-colors"
                  data-testid="nav-dashboard"
                >
                  Go to Dashboard
                </Button>
              )
            ) : (
              // Not logged in
              <>
                <ThemeToggle className="hidden lg:inline-flex" />
                <Button
                  variant="ghost"
                  onClick={() => navigate('/login')}
                  className="hidden lg:flex font-label uppercase tracking-[0.18em] text-[11px] text-[#0A0A0A] dark:text-[#F5F5F5] hover:bg-black/[0.04] dark:hover:bg-white/[0.06] rounded-full px-4 py-2 h-auto"
                  data-testid="nav-signin"
                >
                  Sign In
                </Button>
                <ThemeToggle className="lg:hidden" />
                <Button
                  variant="ghost"
                  onClick={() => navigate('/login')}
                  className="lg:hidden font-label uppercase tracking-[0.16em] text-[11px] text-[#0A0A0A] dark:text-[#F5F5F5] hover:bg-black/[0.04] dark:hover:bg-white/[0.06] rounded-full px-3 py-2 h-auto"
                  data-testid="nav-signin-mobile"
                >
                  Sign In
                </Button>
                <Button
                  onClick={() => navigate('/register')}
                  className="hidden lg:flex font-label uppercase tracking-[0.18em] text-[11px] bg-[#0F62FE] dark:bg-[#2E79FF] text-white hover:bg-[#0A4FCC] dark:hover:bg-[#5C97FF] rounded-full px-5 py-2 h-auto transition-colors"
                  data-testid="nav-getstarted"
                >
                  Start Free Trial
                </Button>
              </>
            )}

            {/* Menu Button - Public pages */}
            {isPublicPage && !user && (
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 text-[#0A0A0A] dark:text-[#F5F5F5] hover:bg-black/[0.04] dark:hover:bg-white/[0.06] rounded-full transition-colors"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            )}
          </div>
        </div>

        {/* Slide-in Panel Overlay */}
        {isPublicPage && !user && mobileMenuOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/30 dark:bg-white/20 z-[60] transition-opacity"
              onClick={() => setMobileMenuOpen(false)}
            ></div>

            {/* Panel */}
            <div className="fixed top-0 right-0 h-full w-72 bg-[#FAFAFA] dark:bg-[#050505] shadow-2xl z-[70] transform transition-transform duration-300 ease-out overflow-y-auto">
              {/* Panel Header */}
              <div className="flex items-center justify-between px-5 py-3.5 border-b border-black/10 dark:border-white/10">
                <span className="font-display text-base text-[#0A0A0A] dark:text-[#F5F5F5]">Menu</span>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-1.5 text-black/40 dark:text-white/45 hover:text-[#0A0A0A] dark:hover:text-[#F5F5F5] hover:bg-black/[0.04] dark:hover:bg-white/[0.06] rounded-full transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="py-3">
                {/* Product */}
                <div className="px-5 mb-3">
                  <div className="font-label text-[10px] text-black/55 dark:text-white/55 uppercase tracking-[0.22em] mb-2">Product</div>
                  <div className="space-y-0.5">
                    {[
                      { path: '/', label: 'Home' },
                      { path: '/features', label: 'Features' },
                      { path: '/security', label: 'Security' },
                      { path: '/pricing', label: 'Pricing' },
                      { path: '/trust', label: 'Trust Center' },
                    ].map(item => (
                      <button
                        key={item.path}
                        onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                        className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${
                          isActive(item.path) ? 'text-[#0A0A0A] dark:text-[#F5F5F5] bg-[#D4E0DA]/55 dark:bg-[#101F18]/55' : 'text-black/70 dark:text-white/70 hover:bg-black/[0.04] dark:hover:bg-white/[0.06] hover:text-[#0A0A0A] dark:hover:text-[#F5F5F5]'
                        }`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mx-5 border-t border-black/10 dark:border-white/10"></div>

                {/* Company */}
                <div className="px-5 my-3">
                  <div className="font-label text-[10px] text-black/55 dark:text-white/55 uppercase tracking-[0.22em] mb-2">Company</div>
                  <div className="space-y-0.5">
                    {[
                      { path: '/about', label: 'About Us' },
                      { path: '/contact', label: 'Contact' },
                    ].map(item => (
                      <button
                        key={item.path}
                        onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                        className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${
                          isActive(item.path) ? 'text-[#0A0A0A] dark:text-[#F5F5F5] bg-[#D4E0DA]/55 dark:bg-[#101F18]/55' : 'text-black/70 dark:text-white/70 hover:bg-black/[0.04] dark:hover:bg-white/[0.06] hover:text-[#0A0A0A] dark:hover:text-[#F5F5F5]'
                        }`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mx-5 border-t border-black/10 dark:border-white/10"></div>

                {/* Trust & Legal */}
                <div className="px-5 my-3">
                  <div className="font-label text-[10px] text-black/55 dark:text-white/55 uppercase tracking-[0.22em] mb-2">Trust &amp; Legal</div>
                  <div className="space-y-0.5">
                    {[
                      { path: '/trust', label: 'Trust Center' },
                      { path: '/privacy-policy', label: 'Privacy Policy' },
                      { path: '/terms-of-service', label: 'Terms of Service' },
                      { path: '/dpa', label: 'Data Processing Agreement (DPA)' },
                    ].map(item => (
                      <button
                        key={item.path}
                        onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                        className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${
                          isActive(item.path) ? 'text-[#0A0A0A] dark:text-[#F5F5F5] bg-[#D4E0DA]/55 dark:bg-[#101F18]/55' : 'text-black/70 dark:text-white/70 hover:bg-black/[0.04] dark:hover:bg-white/[0.06] hover:text-[#0A0A0A] dark:hover:text-[#F5F5F5]'
                        }`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mx-5 border-t border-black/10 dark:border-white/10"></div>

                {/* Auth Buttons */}
                <div className="px-5 mt-4 space-y-2">
                  <Button
                    onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                    variant="outline"
                    size="sm"
                    className="w-full font-label uppercase tracking-[0.18em] text-[11px] border-black/15 dark:border-white/15 text-[#0A0A0A] dark:text-[#F5F5F5] bg-transparent hover:bg-black/[0.04] dark:hover:bg-white/[0.06] rounded-full"
                  >
                    Sign In
                  </Button>
                  <Button
                    onClick={() => { navigate('/register'); setMobileMenuOpen(false); }}
                    size="sm"
                    className="w-full font-label uppercase tracking-[0.18em] text-[11px] bg-[#0F62FE] dark:bg-[#2E79FF] text-white hover:bg-[#0A4FCC] dark:hover:bg-[#5C97FF] rounded-full"
                  >
                    Start Free Trial
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Header;